import type { RevealedRoundType } from './livePutts'
import {
  PATTERN_KEY_RE,
  PUTTS_PER_ROUND,
  ROUND_KEY_RE,
  countFromKey,
  type BirdieTileView,
  type PuttMark
} from './tileViewModel'

/**
 * Live ticket status (B34). Once a putt is on screen the board can say which
 * options the result has already ruled out — an exact pattern whose prefix no
 * longer matches, a count the remaining putts can no longer reach — and, on the
 * third putt, which alive option is the big one still in play. Pure over the
 * marks `boardPhase.livePutts` exposes, which are EMPTY once the settlement is
 * visible, so the settled classes never compete with these.
 */
export type TileLife = 'alive' | 'dead'

export interface LiveStatus {
  /** True while at least one putt is on screen and the round is not settled. */
  active: boolean
  /** Options the putts already on screen have ruled out. */
  deadIds: ReadonlySet<string>
  /**
   * Two in and the third under way: the alive option(s) that pay if the third
   * drops too — "if 2 already went in then the 3rd will lit on fire". Nothing
   * burns after a miss: the streak is the moment, not the price.
   */
  hotIds: ReadonlySet<string>
  /** True while the third putt is under way (two putts seen). */
  thirdPutt: boolean
  /** True while the third putt is under way with BOTH earlier putts holed. */
  streak: boolean
  /**
   * B40 — the options that were ONE BALL from paying when the third rimmed out.
   *
   * A near miss is the most emotional moment this game has and the board drew it
   * exactly like a three-miss: the option that had two in and needed the last
   * one died in the same wave as the options that were never close.
   *
   * It is data and not a guess: two putts holed, the third missed, and the
   * option is one that needed that last ball (`paysIfThirdHoled`) and was still
   * alive going into it. Empty in every other state, including a round where
   * nothing dropped — nothing was close, so nothing sighs.
   */
  nearMissIds: ReadonlySet<string>
}

const EMPTY_SET: ReadonlySet<string> = new Set()

export const IDLE_LIVE_STATUS: LiveStatus = {
  active: false,
  deadIds: EMPTY_SET,
  hotIds: EMPTY_SET,
  thirdPutt: false,
  streak: false,
  nearMissIds: EMPTY_SET
}

export function holedCount(seen: readonly PuttMark[]): number {
  let holed = 0
  for (const mark of seen) if (mark === 'holed') holed++
  return holed
}

/**
 * Whether an option can still pay given the putts already on screen. The putt markets only: a
 * round-type key is one this cannot read, and `liveStatus` resolves those with the revealed type
 * instead — never by dimming them here for want of a reveal.
 */
export function optionAlive(key: string, seen: readonly PuttMark[]): boolean {
  if (seen.length === 0) return true
  if (PATTERN_KEY_RE.test(key)) {
    for (let i = 0; i < seen.length && i < key.length; i++) {
      if ((key.charAt(i) === 'H') !== (seen[i] === 'holed')) return false
    }
    return true
  }
  const made = countFromKey(key)
  // An option the board cannot read is never dimmed.
  if (made === undefined) return true
  const holed = holedCount(seen)
  const remaining = PUTTS_PER_ROUND - seen.length
  return made >= holed && made <= holed + remaining
}

/**
 * Whether a round-type tile can still pay, given the type the putt rows have revealed.
 *
 * The putts say NOTHING about this market — the type came off the seed before a ball was struck —
 * so it is resolved by the reveal alone. An unknown type leaves both alive: no row has arrived yet,
 * or the engine publishes none, and a board that guessed would dim the winner.
 */
export function roundTypeAlive(key: string, revealed: RevealedRoundType | undefined): boolean {
  if (revealed === undefined) return true
  return key.toLowerCase() === revealed
}

export function liveStatus(
  tiles: readonly BirdieTileView[],
  seen: readonly PuttMark[],
  revealed?: RevealedRoundType
): LiveStatus {
  if (seen.length === 0) return IDLE_LIVE_STATUS
  const deadIds = new Set<string>()
  const alive: BirdieTileView[] = []
  for (const tile of tiles) {
    const stillIn = ROUND_KEY_RE.test(tile.key)
      ? roundTypeAlive(tile.key, revealed)
      : optionAlive(tile.key, seen)
    if (stillIn) alive.push(tile)
    else deadIds.add(tile.optionId)
  }
  const thirdPutt = seen.length === PUTTS_PER_ROUND - 1
  const streak = thirdPutt && holedCount(seen) === PUTTS_PER_ROUND - 1
  const hotIds = new Set<string>()
  if (streak) {
    for (const tile of alive) if (paysIfThirdHoled(tile.key)) hotIds.add(tile.optionId)
  }
  return { active: true, deadIds, hotIds, thirdPutt, streak, nearMissIds: nearMiss(tiles, seen) }
}

/**
 * The options that were one ball away when the third rimmed out.
 *
 * Exactly the set `hotIds` held on the previous beat, recomputed from the marks
 * rather than remembered: the board keeps no history, and a set derived from the
 * putts on screen cannot disagree with the putts on screen.
 *
 * The three conditions are the whole definition — the third putt MISSED, both
 * earlier ones dropped, and this option needed that last ball. A round that ends
 * H-M-H has near misses of its own in the loose sense; this is the one the owner
 * means, the one where the player watched two go in.
 */
export function nearMiss(
  tiles: readonly BirdieTileView[],
  seen: readonly PuttMark[]
): ReadonlySet<string> {
  if (seen.length !== PUTTS_PER_ROUND) return EMPTY_SET
  if (seen[PUTTS_PER_ROUND - 1] !== 'missed') return EMPTY_SET
  if (holedCount(seen) !== PUTTS_PER_ROUND - 1) return EMPTY_SET
  const out = new Set<string>()
  const beforeTheLast = seen.slice(0, PUTTS_PER_ROUND - 1)
  for (const tile of tiles) {
    if (ROUND_KEY_RE.test(tile.key)) continue
    if (!paysIfThirdHoled(tile.key)) continue
    if (!optionAlive(tile.key, beforeTheLast)) continue
    out.add(tile.optionId)
  }
  return out
}

/** An option that needs the LAST putt holed: the full count, or a pattern ending in H. */
export function paysIfThirdHoled(key: string): boolean {
  if (PATTERN_KEY_RE.test(key)) return key.charAt(PUTTS_PER_ROUND - 1) === 'H'
  return countFromKey(key) === PUTTS_PER_ROUND
}

/** The life a tile renders, or undefined outside the live window. */
export function tileLife(status: LiveStatus, optionId: string): TileLife | undefined {
  if (!status.active) return undefined
  return status.deadIds.has(optionId) ? 'dead' : 'alive'
}
